/**
 * Weekly Cruise Briefing Card ·
 * INT-001 (MVP) · INT-001.1 v3 Hierarchy Adjustment 2026-08-07.
 *
 * Governance:
 *   - INT-001 authorised by PO 2026-08-07.
 *   - INT-001.1 v1 (initial visual refresh) · v2 (editorial refinement) ·
 *     v3 (this revision) — hierarchy adjustment so the briefing card
 *     reads as the "lead story beneath the primary voyage banner"
 *     rather than a second hero. Presentation-only; NO backend / API /
 *     data-model / placement / authorization / publishing-workflow /
 *     briefing-logic changes. The Voyage Hero (WorkspaceHero.jsx) is
 *     explicitly out of scope and untouched.
 *   - GOV-DASH-001 dashboard-freeze exception in force.
 *
 * Data contract (unchanged from INT-001 MVP):
 *   GET /api/cruises/current/briefing/latest
 *     · 200 → renders the editorial highlight
 *     · 204 → hides entirely
 *     · error → hides silently
 *
 * v3 changes (per PO revision directive 2026-08-07):
 *   - Overall height reduced ~40% (min-height 220 → 140).
 *   - Full-width preserved; content laid out horizontally.
 *   - Hero image reduced to ~28% of the card width (was ~44%).
 *   - Supporting copy shortened to one concise editorial sentence.
 *   - Editorial styling, navy palette, and CC vessel-gold accents retained.
 *   - CTA kept strong but calibrated to remain subordinate to Voyage Hero.
 */
import React, { useEffect, useState } from "react";
import { BookOpen, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCruise } from "@/context/CruiseContext";

const API = process.env.REACT_APP_BACKEND_URL;

const CC = {
  navyDeep:            "#0b132b",
  navy:                "#112240",
  goldEditorial:       "#C28E42",
  goldEditorialHover:  "#B77D30",
  goldGlow:            "rgba(194,142,66,0.28)",
};

// UXP-001 §17-B · Canonical Data Fidelity (ratified 2026-08-12).
// The Weekly Briefing card MUST NOT render a hardcoded photograph as
// a "hero" — even a generic tropical beach — because it silently
// substitutes unrelated content for the traveler's actual voyage.
// If a canonical hero for THIS voyage is needed, it must be resolved
// through the CKB Canonical Hero Resolver keyed on the voyage's own
// ship / itinerary. Otherwise render the branded placeholder (or no
// hero) — never stock imagery.
const HERO_IMAGE_URL = null;

function _humanDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch { return iso; }
}

export default function WeeklyBriefingCard() {
  const { selectedCruiseId, setRoute, voyage } = useCruise();
  const [briefing, setBriefing] = useState(null);
  // "loading" | "ready" | "none" (204 — truly no edition) | "error" — the empty state may only render on "none".
  const [status, setStatus] = useState("loading");
  const [hovered, setHovered] = useState(false);
  const [imageOk, setImageOk] = useState(true);

  useEffect(() => {
    let alive = true;
    setBriefing(null);
    setStatus("loading");
    (async () => {
      try {
        // CKB-040 v4.1 · LAW-005 · axios `api` interceptor auto-injects
        // `?voyage_id=<selectedCruiseId>` — the briefing card follows
        // the Cruise Selector's context, never the deprecated pointer.
        const r = await api.get("/api/cruises/current/briefing/latest",
                                 { validateStatus: (s) => s === 200 || s === 204 || s === 404 });
        if (r.status === 200 && alive && r.data && r.data.title) {
          // RC7 · P0 · Voyage-isolation defensive guard (PO 2026-02-11)
          // Belt-and-braces check: even though the backend now uses
          // `_current_voyage_or_404`, refuse to render a briefing
          // whose `voyage_id` does not match the CruiseContext's
          // selected id. If a mismatch ever leaks through, log a
          // diagnostic and hide the card rather than mislead the
          // traveller with content from another voyage.
          if (selectedCruiseId && r.data.voyage_id && r.data.voyage_id !== selectedCruiseId) {
             
            console.warn(
              "[WeeklyBriefingCard] Voyage isolation guard tripped:",
              "expected voyage_id=", selectedCruiseId,
              "received voyage_id=", r.data.voyage_id,
              "briefing_id=", r.data.id,
            );
            return;
          }
          setBriefing(r.data);
          setStatus("ready");
        } else if (alive) {
          setStatus(r.status === 204 ? "none" : "error");
        }
      } catch {
        // silent hide — the card must never block the dashboard.
        if (alive) setStatus("error");
      }
    })();
    return () => { alive = false; };
  }, [selectedCruiseId]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageOk(true);
    img.onerror = () => setImageOk(false);
    img.src = HERO_IMAGE_URL;
  }, []);

  if (!briefing) {
    if (status !== "none") return null;   // loading / transport error: never claim "first briefing shortly"
    // RC7 · P0 · Eligibility placeholder (PO 2026-02-11)
    // When the backend returned 204 (no briefing exists for this
    // voyage), we surface the reason cleanly instead of silently
    // hiding the surface. This tells the traveller the Weekly
    // Briefing is coming, and under what conditions.
    const bookingStatus = (voyage?.booking_status || "").toLowerCase();
    const embarkIso = voyage?.embark_date || voyage?.embark_at;
    let daysToEmbark = null;
    if (embarkIso) {
      const embark = new Date(embarkIso);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      embark.setHours(0, 0, 0, 0);
      daysToEmbark = Math.round((embark - today) / 86400000);
    }
    let message = null;
    if (voyage && bookingStatus && bookingStatus !== "booked") {
      message = "Available once your cruise is booked.";
    } else if (voyage && daysToEmbark != null && daysToEmbark > 120) {
      message = `Available once your cruise is within 120 days of sailing (currently ${daysToEmbark} days away).`;
    } else if (voyage) {
      // Voyage is booked and within window but the briefing hasn't
      // been generated yet — cron will produce it on its next run.
      message = "Your first Weekly Briefing will be published shortly.";
    }
    if (!message) return null;
    return (
      <section
        data-testid="voyage-home-weekly-briefing-eligibility-placeholder"
        className="rounded-2xl border border-slate-200 bg-white px-6 py-6"
      >
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#C28E42]">
          Weekly Briefing
        </div>
        <p className="mt-2 text-[14px] leading-relaxed text-[#475569]">
          {message}
        </p>
      </section>
    );
  }

  // RC7 Briefing Engine (PO 2026-02-11) — the primary reading path
  // is the dedicated in-app reader (`briefing:<id>` route). The
  // legacy `external_url` (ChatGPT link) is intentionally NOT the
  // primary CTA; it lives inside the reader as a secondary link.
  const handleOpen = () => {
    if (briefing.id) {
      setRoute(`briefing:${briefing.id}`);
    }
  };

  // Prefer engine-generated editorial fields when present; fall back
  // gracefully to the legacy record shape for pre-RC7 briefings.
  const editorialHeadline = briefing.headline || "This Week's Cruise Intelligence";
  const summarySentence =
    briefing.executive_summary ||
    "Curated voyage intelligence, refreshed weekly.";

  return (
    <div className="mt-6" data-testid="voyage-home-weekly-briefing">
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-testid="voyage-home-weekly-briefing-card"
        className="relative overflow-hidden rounded-xl shadow-md transition-shadow duration-300 hover:shadow-lg"
        style={{
          background: `linear-gradient(135deg, ${CC.navyDeep} 0%, ${CC.navy} 65%, #16305a 100%)`,
          minHeight: "140px",
        }}
      >
        {/* Panoramic hero image — narrower band (~28%) on the right */}
        {imageOk && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 hidden sm:block transition-transform duration-700 ease-out"
            style={{
              width: "30%",
              backgroundImage: `url("${HERO_IMAGE_URL}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              transform: hovered ? "scale(1.03)" : "scale(1)",
              transformOrigin: "center right",
            }}
          />
        )}
        {/* Navy gradient overlay preserving text legibility on the content side */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 hidden sm:block"
          style={{
            background: `linear-gradient(to right, ${CC.navyDeep} 0%, ${CC.navyDeep} 58%, rgba(11,19,43,0.85) 70%, rgba(11,19,43,0.35) 88%, rgba(11,19,43,0.15) 100%)`,
          }}
        />
        {/* Soft warm horizon glow behind the CTA (bottom-left) — subtle */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{
            left: "-6%",
            bottom: "-60%",
            width: "55%",
            height: "140%",
            background: `radial-gradient(closest-side, ${CC.goldGlow} 0%, rgba(194,142,66,0.04) 45%, rgba(11,19,43,0) 100%)`,
          }}
        />
        {/* Editorial masthead hairline */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(to right, rgba(194,142,66,0) 0%, rgba(194,142,66,0.55) 35%, rgba(194,142,66,0.55) 65%, rgba(194,142,66,0) 100%)",
          }}
        />

        {/* CONTENT — text stack (button lives outside this constrained
            region so it can anchor to the CARD's bottom-right corner,
            not the 72%-content-region's bottom-right corner). */}
        <div
          className="relative flex flex-col gap-3 px-5 sm:px-6 py-4 sm:py-5"
          style={{ maxWidth: "72%", minHeight: "140px" }}
        >
          <div className="min-w-0 flex-1">
            {/* Section label */}
            <div
              className="flex items-center gap-2 text-[9.5px] font-semibold uppercase tracking-[0.26em]"
              style={{ color: CC.goldEditorial }}
              data-testid="voyage-home-weekly-briefing-section-label"
            >
              <span>Cruise Intelligence</span>
              <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.28)" }}>·</span>
              <span
                className="font-medium tracking-[0.14em]"
                style={{ color: "rgba(255,255,255,0.5)" }}
                data-testid="voyage-home-weekly-briefing-date"
              >
                {_humanDate(briefing.published_date)}
              </span>
            </div>

            {/* Primary editorial headline (compact) — engine-generated when available */}
            <h3
              className="mt-3 font-display text-white leading-tight tracking-tight"
              style={{
                fontSize: "clamp(1.05rem, 1.55vw, 1.25rem)",
                fontWeight: 600,
                letterSpacing: "-0.012em",
              }}
              data-testid="voyage-home-weekly-briefing-editorial-headline"
            >
              {editorialHeadline}
            </h3>

            {/* Kicker (database title) */}
            <div
              className="mt-0.5 font-body truncate"
              style={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "12px",
                fontWeight: 400,
                maxWidth: "58ch",
              }}
              data-testid="voyage-home-weekly-briefing-title"
            >
              {briefing.title}
            </div>

            {/* Executive summary (RC7) or legacy invitation */}
            <p
              className="mt-1.5 font-body leading-snug line-clamp-2"
              style={{
                color: "rgba(255,255,255,0.78)",
                fontSize: "12.5px",
                maxWidth: "62ch",
              }}
              data-testid="voyage-home-weekly-briefing-invitation"
            >
              {summarySentence}
            </p>
          </div>

          {/* CTA — Note: closed the content stack ABOVE this line.
              Kept blank so the button below anchors to the CARD, not
              this constrained content region. */}
        </div>

        {/* CTA — absolute bottom-right of the CARD (PO 2026-08-14
            correction: was previously nested inside the 72%-max-width
            content region, which made it appear centered on the card).
            Uses the same padding rhythm as the content region. */}
        <div
          className="absolute right-5 bottom-4 sm:right-6 sm:bottom-5 z-10"
          data-testid="voyage-home-weekly-briefing-cta-anchor"
        >
          <button
            type="button"
            onClick={handleOpen}
            data-testid="voyage-home-weekly-briefing-open"
            className="inline-flex items-center gap-2 rounded-full font-semibold transition-all duration-200 whitespace-nowrap"
            style={{
              backgroundColor: hovered ? CC.goldEditorialHover : CC.goldEditorial,
              color: "#ffffff",
              paddingLeft: "14px",
              paddingRight: "14px",
              paddingTop: "7px",
              paddingBottom: "7px",
              fontSize: "11.5px",
              letterSpacing: "0.01em",
              boxShadow: hovered
                ? "0 8px 18px -10px rgba(194,142,66,0.55), 0 3px 6px -2px rgba(0,0,0,0.26)"
                : "0 3px 10px -6px rgba(194,142,66,0.35), 0 2px 4px -2px rgba(0,0,0,0.2)",
              transform: hovered ? "translateY(-1px)" : "translateY(0)",
            }}
            aria-label="Open this week's Weekly Cruise Briefing"
          >
            <BookOpen className="w-3 h-3" strokeWidth={2.25} />
            <span>Read Weekly Briefing</span>
            <ArrowRight
              className="w-3 h-3 transition-transform duration-200"
              strokeWidth={2.5}
              style={{ transform: hovered ? "translateX(3px)" : "translateX(0)" }}
            />
          </button>
        </div>

        {/* -- Legacy inline CTA slot removed (PO 2026-08-14). --*/}
      </aside>
    </div>
  );
}
